import { onMounted, onUnmounted, type Ref } from "vue";

/** Drive the `.anim` → `.anim.is-in` cascade for a screen.
 *
 * Each child element with class `anim` and a numeric `data-delay` (ms)
 * gets the `is-in` class added at that delay relative to mount. Elements
 * without `data-delay` reveal immediately on the next frame.
 *
 * MutationObserver catches `.anim` elements added after the initial mount
 * (e.g. when the async data fetch resolves and a v-if branch swaps in).
 * Without this, screens that block their primary body on data — Project,
 * Sprint — render the body invisibly transparent because the first scan
 * happens before the body exists.
 *
 * Honours prefers-reduced-motion: snaps everything in instantly. */
export function useReveal(root: Ref<HTMLElement | null>): void {
  let observer: MutationObserver | null = null;

  onMounted(() => {
    const host = root.value;
    if (!host) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function reveal(el: HTMLElement): void {
      // Idempotent: an element only reveals once. The `is-in` class is the
      // sentinel — if it's already there, the scheduled timeout is a no-op
      // anyway, but skipping cuts the timer churn during big DOM swaps.
      if (el.dataset.revealQueued === "1") return;
      el.dataset.revealQueued = "1";

      if (reduced) {
        el.classList.add("is-in");
        return;
      }
      const d = Number(el.dataset.delay ?? "0") || 0;
      window.setTimeout(() => el.classList.add("is-in"), d);
    }

    function scan(): void {
      host!.querySelectorAll<HTMLElement>(".anim").forEach(reveal);
    }

    scan();
    observer = new MutationObserver(scan);
    observer.observe(host, { childList: true, subtree: true });
  });

  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
  });
}
